import {
  computed,
  ref,
  reactive,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { useCheckoutStore } from '@/stores/useCheckoutStore';
import { useUIStore } from '@/stores/useUIStore';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/ca';
import CartService from '@/services/cartService';
import CustomerHelper from '@/helpers/customerHelper';
import { fetchCheckoutInfo, validatePromo } from '@/services/checkoutService';
import { fetchLegalSettings } from '@/services/legalSettingsService';
import { saveBookingComment } from '@/services/bookingCommentService';
import type { SaleEntry, CheckoutContext, CartState, CheckoutInfoResponse } from '@/types/checkout';

export function useCheckoutData() {
  const checkoutStore = useCheckoutStore();
  const uiStore = useUIStore();
  const router = useRouter();
  const { t } = useI18n();

  const loadingInfo = ref<boolean>(false);
  const infoError = ref<string | null>(null);
  const checkoutInfo = ref<CheckoutInfoResponse | null>(null);
  const lastCheckoutInfoKey = ref<string | null>(null);
  const legalSettings = ref<Record<string, unknown> | null>(null);
  const promoCode = ref<string>('');
  const promoError = ref<string | null>(null);
  const promoSuccess = ref<string | null>(null);
  const promoDiscount = ref<number>(0);
  const promoApplied = ref<boolean>(false);
  const mobileSummaryOpen = ref<boolean>(false);
  const summaryTargetExists = ref<boolean>(false);
  const personalComment = ref<string>('');
  const commentSaveTimer = ref<number | null>(null);
  const lastSavedComment = ref<string>('');
  const lastSavedBookingId = ref<number | string | null>(null);

  const legalModal = reactive({
    visible: false,
    title: '',
    content: '',
    loading: false,
  });

  const checkoutContext = computed<CheckoutContext | null>(() => checkoutStore.checkoutContext as CheckoutContext | null || null);
  const cart = computed<CartState | null>(() => checkoutStore.cart as CartState | null || null);

  const showGlobalError = (message: string | null) => {
    if (!message) return;
    try {
      uiStore.setGlobalErrorData({ response: { status: -1, message } });
      uiStore.openGlobalError();
    } catch (error) {
      void error;
    }
  };

  const resolveErrorMessage = (error: unknown): string | null => {
    if (!error) {
      return null;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    if (typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;
      return typeof message === 'string' ? message : null;
    }
    return null;
  };

  const resolveBackendErrorMessage = (error: unknown): string | null => {
    if (!error || typeof error !== 'object' || !('response' in error)) {
      return null;
    }
    const response = (error as { response?: { data?: unknown } }).response;
    const data = response?.data;
    if (!data || typeof data !== 'object') {
      return null;
    }
    const errorMsg = (data as { errorMsg?: unknown }).errorMsg;
    if (typeof errorMsg === 'string' && errorMsg.trim()) {
      return errorMsg;
    }
    const modelErrorMsg = (data as { model?: { errorMsg?: unknown } }).model?.errorMsg;
    if (typeof modelErrorMsg === 'string' && modelErrorMsg.trim()) {
      return modelErrorMsg;
    }
    return null;
  };

  const sales = computed<SaleEntry[]>(() => {
    if (cart.value?.sales?.length) {
      return cart.value.sales;
    }
    const contextSale = checkoutContext.value?.sale;
    return contextSale ? [contextSale] : [];
  });

  const hasSales = computed(() => sales.value.length > 0);

  const saleIds = computed<Array<number | string>>(() => sales.value
    .map((sale: SaleEntry) => sale?.id)
    .filter((id): id is number | string => id !== undefined && id !== null));

  const currentSale = computed(() => sales.value[0] ?? null);

  const saleDeposits = computed<Record<string, number>>(() => {
    const deposits = checkoutInfo.value?.data?.deposits;
    if (!Array.isArray(deposits)) {
      return {};
    }
    const map: Record<string, number> = {};
    deposits.forEach((entry) => {
      const id = entry?.saleId;
      const amount = Number(entry?.depositAmount);
      if (id !== undefined && id !== null && Number.isFinite(amount) && amount > 0) {
        map[String(id)] = amount;
      }
    });
    return map;
  });

  const tenantUrl = computed(() => {
    if (sales.value.length > 0 && sales.value[0]?.tenantUrl) {
      return sales.value[0].tenantUrl;
    }
    return checkoutContext.value?.tenantUrl || null;
  });

  const normalizeText = (value: unknown): string => {
    if (typeof value !== 'string') {
      return '';
    }
    return value.replace(/\s+/g, ' ').trim();
  };

  const buildDateFromParts = (datePart: string, timePart: string): string | null => {
    const datePieces = datePart.split('/');
    if (datePieces.length !== 3) {
      return null;
    }
    const day = Number(datePieces[0]);
    const month = Number(datePieces[1]);
    const yearPart = datePieces[2];
    let year = Number(yearPart);
    if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) {
      return null;
    }
    if (yearPart.length === 2) {
      year += 2000;
    }
    const timePieces = timePart.split(':');
    const hour = Number(timePieces[0]);
    const minute = Number(timePieces[1]);
    const second = Number(timePieces[2] || 0);
    if (!Number.isFinite(hour) || !Number.isFinite(minute) || !Number.isFinite(second)) {
      return null;
    }
    const pad = (value: number) => String(value).padStart(2, '0');
    const hasSeconds = timePieces.length > 2;
    const normalizedTime = `${pad(hour)}:${pad(minute)}${hasSeconds ? `:${pad(second)}` : ''}`;
    return `${pad(day)}/${pad(month)}/${year} ${normalizedTime}`;
  };

  const extractAppointmentDetails = (description: unknown) => {
    const text = normalizeText(description);
    if (!text) {
      return {
        service: null,
        date: null,
        duration: null,
        specialist: null,
      };
    }

    const dateMatch = text.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})\s+(\d{1,2}:\d{2}(?::\d{2})?)/);
    if (!dateMatch || typeof dateMatch.index !== 'number') {
      return {
        service: text,
        date: null,
        duration: null,
        specialist: null,
      };
    }

    const before = text.slice(0, dateMatch.index).trim();
    const after = text.slice(dateMatch.index + dateMatch[0].length).trim();
    const dateValue = buildDateFromParts(dateMatch[1], dateMatch[2]) || `${dateMatch[1]} ${dateMatch[2]}`;
    const durationMatch = after.match(/(\d+)\s*(Minutos|min)/i);
    let duration = null;
    let specialist = null;

    if (durationMatch && typeof durationMatch.index === 'number') {
      duration = `${durationMatch[1]} min`;
      const remainder = after.slice(durationMatch.index + durationMatch[0].length).trim();
      specialist = remainder || null;
    } else {
      specialist = after || null;
    }

    return {
      service: before || text,
      date: dateValue,
      duration,
      specialist,
    };
  };

  const parseDescriptionLines = (rawValue: unknown) => {
    if (!rawValue) {
      return [];
    }
    const parts = String(rawValue).split(/\s{2,}/).map((item) => item.trim()).filter(Boolean);
    if (parts.length >= 4) {
      const [servicePart, dateTimePart, durationPart, ...rest] = parts;
      const specialistPart = rest.join(' ');
      return [servicePart, dateTimePart, durationPart, specialistPart].filter(Boolean);
    }
    return parts;
  };

  const currencyFormatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'EUR',
  });

  const formatAmount = (rawValue: unknown) => {
    const amount = Number(rawValue);
    if (!Number.isFinite(amount)) {
      return rawValue === undefined || rawValue === null ? '-' : String(rawValue);
    }
    try {
      return currencyFormatter.format(amount);
    } catch {
      return amount.toFixed(2);
    }
  };

  const formatSaleDate = (raw: unknown, useLongFormat = false): string | null => {
    if (!raw) {
      return null;
    }
    const locale = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'es';

    const parseCandidate = (value: unknown) => {
      if (!value) return null;
      const str = String(value).trim();
      const slashMatch = str.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s+|T)(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (slashMatch) {
        const day = Number(slashMatch[1]);
        const month = Number(slashMatch[2]);
        const yearPart = slashMatch[3];
        let year = Number(yearPart);
        const hour = Number(slashMatch[4]);
        const minute = Number(slashMatch[5]);
        const second = Number(slashMatch[6] || 0);
        if (yearPart.length === 2) {
          year += 2000;
        }
        const parsed = new Date(year, month - 1, day, hour, minute, second);
        if (!Number.isNaN(parsed.getTime())) {
          return dayjs(parsed);
        }
      }
      if (dayjs(str).isValid()) {
        return dayjs(str);
      }
      const [first] = str.split(/\s+/);
      if (first && dayjs(first).isValid()) {
        return dayjs(first);
      }
      return null;
    };

    const parsed = raw instanceof Date ? dayjs(raw) : parseCandidate(raw);
    if (!parsed || !parsed.isValid()) {
      return raw !== undefined && raw !== null ? String(raw) : null;
    }
    const formatPattern = useLongFormat ? 'dddd, D [de] MMMM [de] YYYY HH:mm' : 'DD MMM YYYY, HH:mm';
    return parsed.locale(locale).format(formatPattern);
  };

  const normalizePersonalValue = (value: unknown): string => {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  };

  const personalName = computed<string>(() => {
    const checkoutName = checkoutInfo.value?.data?.name;
    if (checkoutName) {
      return normalizePersonalValue(checkoutName);
    }
    const contextName = checkoutContext.value?.sale?.customer?.name;
    if (contextName) {
      return normalizePersonalValue(contextName);
    }
    const user = CustomerHelper.getUser();
    if (user && user.name) {
      return normalizePersonalValue(user.name);
    }
    const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}') as Record<string, unknown>;
    return normalizePersonalValue(storedUserData.name);
  });

  const personalSurname = computed<string>(() => {
    const checkoutSurname = checkoutInfo.value?.data?.surname;
    if (checkoutSurname) {
      return normalizePersonalValue(checkoutSurname);
    }
    const contextSurname = checkoutContext.value?.sale?.customer?.surname;
    if (contextSurname) {
      return normalizePersonalValue(contextSurname);
    }
    const user = CustomerHelper.getUser();
    if (user && user.surname) {
      return normalizePersonalValue(user.surname);
    }
    const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}') as Record<string, unknown>;
    return normalizePersonalValue(storedUserData.surname);
  });

  const normalizeCommentValue = (value: string | null): string => {
    if (typeof value !== 'string') {
      return '';
    }
    return value.trim();
  };

  const resolveCommentBookingId = (): number | string | null => {
    if (!sales.value.length) {
      return null;
    }
    const sale = sales.value[0] as SaleEntry | null;
    if (!sale) {
      return null;
    }
    const bookingId = sale.bookingId ?? (sale as { BookingId?: number | string | null }).BookingId ?? null;
    if (bookingId === undefined || bookingId === null) {
      return null;
    }
    return bookingId;
  };

  const buildCommentPayload = (commentValue: string) => {
    const bookingId = resolveCommentBookingId();
    if (bookingId === null) {
      return null;
    }
    return {
      bookingId,
      tenantUrl: tenantUrl.value,
      comment: commentValue ? commentValue : null,
    };
  };

  const saveCommentNow = async (commentValue: string) => {
    const payload = buildCommentPayload(commentValue);
    if (!payload) {
      return;
    }
    try {
      const response = await saveBookingComment(payload);
      const hasError = response
        && typeof response === 'object'
        && (response as { ok?: boolean }).ok === false;
      if (hasError) {
        return;
      }
      lastSavedComment.value = commentValue;
      lastSavedBookingId.value = payload.bookingId;
    } catch {
      return;
    }
  };

  const scheduleCommentSave = () => {
    if (typeof window === 'undefined') {
      return;
    }
    const bookingId = resolveCommentBookingId();
    if (bookingId === null) {
      return;
    }
    const normalized = normalizeCommentValue(personalComment.value);
    if (normalized === normalizeCommentValue(lastSavedComment.value)
      && bookingId === lastSavedBookingId.value) {
      return;
    }
    if (commentSaveTimer.value !== null) {
      clearTimeout(commentSaveTimer.value);
      commentSaveTimer.value = null;
    }
    commentSaveTimer.value = window.setTimeout(() => {
      commentSaveTimer.value = null;
      saveCommentNow(normalized);
    }, 700);
  };

  const saleDoctorCards = computed(() => {
    const singleSale = sales.value.length === 1;
    const contextTherapist = checkoutContext.value?.therapist ?? null;
    const contextType = checkoutContext.value?.type ?? null;

    return sales.value.map((sale: SaleEntry) => {
      const id = sale?.id ?? sale?.saleNumber ?? sale?.bookingId ?? Math.random();
      const saleId = sale?.id ?? sale?.saleNumber ?? sale?.bookingId ?? null;
      const reservationType = sale?.reservationType ?? (singleSale ? contextType : null);
      const appointmentDetails = reservationType === 'class'
        ? { service: null, date: null, duration: null, specialist: null }
        : extractAppointmentDetails(sale?.description);

      let specialist = sale?.specialistName
        ?? sale?.therapistName
        ?? sale?.therapist?.name
        ?? (singleSale ? checkoutContext.value?.specialistName : null)
        ?? null;
      if (!specialist && appointmentDetails.specialist) {
        specialist = appointmentDetails.specialist;
      }

      const bookingTypeName = sale?.bookingTypeName ?? null;
      const contextServiceName = singleSale ? checkoutContext.value?.serviceName : null;
      let service = bookingTypeName
        || contextServiceName
        || sale?.serviceName
        || sale?.classTitle
        || null;
      if (!service && reservationType === 'class') {
        service = sale?.description || null;
      }
      if (!service && appointmentDetails.service) {
        service = appointmentDetails.service;
      }
      if (!service) {
        service = sale?.description
          || sale?.priceLabel
          || (sale?.saleNumber ? `${t('Sale')} #${sale.saleNumber}` : t('Sale'));
      }

      let date = sale?.startDisplay ?? sale?.date ?? null;
      if (!date) {
        const slot = (sale && typeof sale === 'object' ? sale.slot : null)
          || (singleSale && checkoutContext.value && typeof checkoutContext.value === 'object' ? checkoutContext.value.slot : null);
        if (slot && typeof slot === 'object') {
          const datePart = slot.date;
          const hourPart = slot.hour;
          date = [datePart, hourPart].filter(Boolean).join(' ');
        }
      }
      const normalizedService = normalizeText(service);
      if (!bookingTypeName && !contextServiceName && normalizedService
        && /(\d{1,2}\/\d{1,2}\/\d{2,4})\s+(\d{1,2}:\d{2})/.test(normalizedService)) {
        const parsedServiceDetails = extractAppointmentDetails(normalizedService);
        if (parsedServiceDetails.service) {
          service = parsedServiceDetails.service;
        }
        if (parsedServiceDetails.date) {
          appointmentDetails.date = appointmentDetails.date || parsedServiceDetails.date;
        }
        if (parsedServiceDetails.duration) {
          appointmentDetails.duration = appointmentDetails.duration || parsedServiceDetails.duration;
        }
        if (!specialist && parsedServiceDetails.specialist) {
          specialist = parsedServiceDetails.specialist;
        }
      }

      if (appointmentDetails.date) {
        date = appointmentDetails.date;
      }
      const useLongFormat = reservationType !== 'class';
      date = formatSaleDate(date, useLongFormat);
      let duration = sale?.durationLabel ?? (singleSale ? checkoutContext.value?.durationLabel : null) ?? null;
      if (!duration && appointmentDetails.duration) {
        duration = appointmentDetails.duration;
      }

      const saleTherapist = sale?.therapist ?? null;
      const saleAvatarFallback = sale && typeof sale === 'object'
        ? (sale.therapistAvatarUrl || sale.avatarUrl || sale.image || sale.photo || sale.userAvatarUrl || null)
        : null;
      const avatar = saleTherapist?.avatarUrl
        || saleTherapist?.image
        || saleTherapist?.photo
        || saleAvatarFallback
        || (singleSale ? (contextTherapist?.avatarUrl || contextTherapist?.image || contextTherapist?.photo) : null)
        || null;
      let typeLabel = null;
      if (reservationType === 'class') {
        typeLabel = t('Class');
      } else if (reservationType) {
        typeLabel = t('Appointment');
      }

      const saleOnline = sale?.isOnline ?? null;
      const contextOnline = singleSale ? (checkoutContext.value?.isOnline ?? null) : null;
      const onlineFlag = saleOnline !== null ? saleOnline : contextOnline;
      let modeLabel = null;
      if (onlineFlag === true) {
        modeLabel = t('Online');
      } else if (onlineFlag === false) {
        modeLabel = t('In-person');
      }

      const descriptionLines = reservationType !== 'class' || (service && date && duration && specialist)
        ? []
        : parseDescriptionLines(sale?.description);
      let raw = null;
      try {
        raw = JSON.stringify(sale, null, 2);
      } catch {
        raw = null;
      }
      const pendingValue = Number(sale?.pending ?? sale?.totalWithDiscount ?? 0) || 0;
      const depositAmount = sale?.id !== undefined && sale?.id !== null
        ? saleDeposits.value[String(sale.id)] ?? null
        : null;

      return {
        id,
        doctorName: specialist || '-',
        service,
        saleNumber: sale?.saleNumber ?? null,
        saleId,
        tenant: sale?.tenantName ?? (singleSale ? checkoutContext.value?.tenantName : null) ?? tenantUrl.value ?? null,
        pendingLabel: formatAmount(sale?.pending ?? sale?.totalWithDiscount ?? 0),
        depositAmount,
        depositLabel: depositAmount !== null ? formatAmount(depositAmount) : null,
        depositRestLabel: depositAmount !== null ? formatAmount(Math.max(pendingValue - depositAmount, 0)) : null,
        date,
        duration,
        avatar,
        typeLabel,
        modeLabel,
        descriptionLines,
        raw,
      };
    });
  });

  const buildCustomerPayload = () => {
    const data = checkoutInfo.value?.data ?? {};
    const user = CustomerHelper.getUser ? CustomerHelper.getUser() : null;
    return {
      id: checkoutContext.value?.sale?.customerId ?? null,
      name: data.name ?? user?.name ?? null,
      surname: data.surname ?? user?.surname ?? null,
      email: data.email ?? user?.email ?? null,
      phone: data.phone ?? user?.phone ?? null,
      addressLine1: data.addressLine1 ?? user?.addressLine1 ?? null,
      town: data.town ?? user?.town ?? null,
      city: data.city ?? user?.city ?? null,
      country: data.country ?? user?.country ?? null,
      postalCode: data.postalCode ?? user?.postalCode ?? null,
    };
  };

  const loadCheckoutInfo = async () => {
    if (!saleIds.value.length || !tenantUrl.value) {
      checkoutInfo.value = null;
      return;
    }
    const requestKey = JSON.stringify({
      tenant: tenantUrl.value,
      sales: [...saleIds.value].sort(),
      customerId: CustomerHelper.resolveCustomerId(),
    });
    if (requestKey && lastCheckoutInfoKey.value === requestKey) {
      return;
    }
    lastCheckoutInfoKey.value = requestKey;
    promoError.value = null;
    promoSuccess.value = null;
    promoDiscount.value = 0;
    promoApplied.value = false;
    loadingInfo.value = true;
    infoError.value = null;
    try {
      const response = await fetchCheckoutInfo({
        tenantUrl: tenantUrl.value,
        saleIds: saleIds.value,
        saleItems: [],
        customer: buildCustomerPayload(),
      });
      if (response && response.ok === false) {
        const backendError = response.errorMsg || response.model?.errorMsg || null;
        infoError.value = backendError || t('Unable to load checkout info');
        checkoutInfo.value = response;
        return;
      }
      checkoutInfo.value = response ?? null;
    } catch (error: unknown) {
      const message = resolveErrorMessage(error);
      infoError.value = message ?? t('Unable to load checkout info');
      checkoutInfo.value = null;
      lastCheckoutInfoKey.value = null;
    } finally {
      loadingInfo.value = false;
    }
  };

  const clearCartAndRedirect = async (
    onClear?: () => void,
  ) => {
    const ids = [...saleIds.value];
    for (const id of ids) {
      await CartService.removeSale(id, { cancelServer: false });
    }
    checkoutStore.setCheckoutContext(null);
    checkoutInfo.value = null;
    onClear?.();
    promoCode.value = '';
    promoError.value = null;
    promoSuccess.value = null;
    promoDiscount.value = 0;
    promoApplied.value = false;
    const cartState = CartService.getCart ? CartService.getCart() : null;
    const hasSalesLeft = Boolean(cartState && Array.isArray(cartState.sales) && cartState.sales.length);
    if (hasSalesLeft) {
      router.push({ name: 'CheckoutPrebookSuccess' });
    } else {
      router.push({ name: 'BookingSuccess' });
    }
  };

  const handleRemoveSale = async (
    saleId: number | string,
    onRemove?: () => void,
  ) => {
    try {
      await CartService.removeSale(saleId, { cancelServer: true });
      if (!checkoutStore.cart?.sales?.length) {
        checkoutStore.setCheckoutContext(null);
        checkoutInfo.value = null;
        onRemove?.();
        router.push({ name: 'Search' });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : null;
      infoError.value = message || t('Unable to remove reservation');
    }
  };

  const applyPromo = async () => {
    promoError.value = null;
    promoSuccess.value = null;
    promoDiscount.value = 0;
    promoApplied.value = false;
    if (!promoCode.value || !promoCode.value.trim()) {
      promoError.value = t('Please enter a coupon code');
      return;
    }
    if (!tenantUrl.value) {
      promoError.value = t('Unable to load checkout info');
      return;
    }
    try {
      const response = await validatePromo({
        tenantUrl: tenantUrl.value,
        code: promoCode.value.trim(),
        saleIds: saleIds.value,
        saleItems: [],
        customerId: checkoutContext.value?.sale?.customerId ?? null,
      });
      if (!response) {
        promoError.value = t('Unable to validate coupon');
        return;
      }
      if (response.ok === false) {
        promoError.value = response.errorMsg || t('Sorry, the code is not valid');
        return;
      }
      const discount = Number(response.data?.discount ?? response.discount ?? 0);
      promoDiscount.value = Number.isFinite(discount) && discount > 0 ? discount : 0;
      promoApplied.value = true;
      promoSuccess.value = t('Discount applied');
    } catch (error: unknown) {
      promoError.value = resolveErrorMessage(error) ?? t('Sorry, the code is not valid');
      promoApplied.value = false;
      promoDiscount.value = 0;
    }
  };

  const legalContentMap = {
    general: { key: 'generalConditions', titleKey: 'general conditions' },
    wayver: { key: 'wayver', titleKey: 'wayver and release' },
    privacy: { key: 'privacyPolicyFull', titleKey: 'privacy policy' },
    cookies: { key: 'cookiesPolicy', titleKey: 'cookie policy' },
  };

  const openLegalModal = async (type: keyof typeof legalContentMap) => {
    const config = legalContentMap[type];
    if (!config) {
      return;
    }
    legalModal.visible = true;
    legalModal.loading = true;
    legalModal.title = t(config.titleKey);
    try {
      if (!legalSettings.value) {
        legalSettings.value = await fetchLegalSettings();
      }
      const payload = legalSettings.value || {};
      const contentValue = payload[config.key];
      legalModal.content = typeof contentValue === 'string' && contentValue.trim().length > 0
        ? contentValue
        : `<p>${t('Legal content is not available right now.')}</p>`;
    } catch {
      legalModal.content = `<p>${t('Unable to load legal information')}</p>`;
    } finally {
      legalModal.loading = false;
    }
  };

  const closeLegalModal = () => {
    legalModal.visible = false;
  };

  onMounted(async () => {
    CartService.ensureCart();
    if (saleIds.value.length) {
      await loadCheckoutInfo();
    }
    if (typeof document !== 'undefined') {
      summaryTargetExists.value = Boolean(document.querySelector('#checkout-summary-panel'));
    }
  });

  watch(personalComment, () => {
    scheduleCommentSave();
  });

  watch(saleIds, async (newIds, oldIds) => {
    const newKey = JSON.stringify(newIds || []);
    const oldKey = JSON.stringify(oldIds || []);
    if (newKey !== oldKey && newIds.length) {
      await loadCheckoutInfo();
    }
    if (!newIds.length) {
      checkoutInfo.value = null;
      promoCode.value = '';
      promoError.value = null;
      promoSuccess.value = null;
      promoDiscount.value = 0;
      promoApplied.value = false;
    }
  });

  onBeforeUnmount(() => {
    if (commentSaveTimer.value !== null) {
      clearTimeout(commentSaveTimer.value);
      commentSaveTimer.value = null;
    }
    const bookingId = resolveCommentBookingId();
    if (bookingId === null) {
      return;
    }
    const normalized = normalizeCommentValue(personalComment.value);
    if (normalized === normalizeCommentValue(lastSavedComment.value)
      && bookingId === lastSavedBookingId.value) {
      return;
    }
    saveCommentNow(normalized);
  });

  return {
    t,
    checkoutContext,
    cart,
    sales,
    hasSales,
    saleIds,
    currentSale,
    checkoutInfo,
    saleDeposits,
    loadingInfo,
    infoError,
    lastCheckoutInfoKey,
    tenantUrl,
    personalName,
    personalSurname,
    personalComment,
    saleDoctorCards,
    promoCode,
    promoError,
    promoSuccess,
    promoDiscount,
    promoApplied,
    mobileSummaryOpen,
    summaryTargetExists,
    legalModal,
    formatAmount,
    formatSaleDate,
    normalizeText,
    showGlobalError,
    resolveErrorMessage,
    resolveBackendErrorMessage,
    loadCheckoutInfo,
    buildCustomerPayload,
    applyPromo,
    handleRemoveSale,
    clearCartAndRedirect,
    openLegalModal,
    closeLegalModal,
  };
}
